import { Injectable } from '@angular/core';
import { Observable, of, delay } from 'rxjs';
import { TeamService } from './team.service';
import { TeamInterviewerItem, TeamItem, TeamMemberItem, TeamUpsertPayload } from '../../interfaces/team/team.interface';
import { PageResponse } from '../../../../core/interfaces/page-response.interface';

@Injectable({
    providedIn: 'root'
})
export class TeamMockService extends TeamService {
    private interviewers: TeamInterviewerItem[] = [
        { uniqueId: 'u-101', interviewerName: 'Ravi Kumar', interviewType: 'TECHNICAL', isRepeatWeekly: true, isActive: true, skills: ['html', 'css'] },
        { uniqueId: 'u-102', interviewerName: 'Sneha Devi', interviewType: 'HR', isRepeatWeekly: false, isActive: true, skills: ['communication'] },
        { uniqueId: 'u-201', interviewerName: 'Akash Verma', interviewType: 'TECHNICAL', isRepeatWeekly: true, isActive: true, skills: ['angular', 'typescript'] },
        { uniqueId: 'u-202', interviewerName: 'Meera Nair', interviewType: 'APTITUDE', isRepeatWeekly: false, isActive: false, skills: ['logic'] },
        { uniqueId: 'u-301', interviewerName: 'John Mathew', interviewType: 'TECHNICAL', isRepeatWeekly: true, isActive: true, skills: ['java', 'spring'] }
    ];

    private teams: TeamItem[] = [
        { uniqueId: 'team-1', name: 'Placement Team A', isActive: true },
        { uniqueId: 'team-2', name: 'Placement Team B', isActive: true },
        { uniqueId: 'team-3', name: 'Backup Team', isActive: false }
    ];

    private membersByTeam: Record<string, TeamMemberItem[]> = {
        'team-1': [
            { uniqueId: 'm-1', teamUniqueId: 'team-1', teamName: 'Placement Team A', interviewerUniqueId: 'u-101', interviewerName: 'Ravi Kumar', isActive: true },
            { uniqueId: 'm-2', teamUniqueId: 'team-1', teamName: 'Placement Team A', interviewerUniqueId: 'u-102', interviewerName: 'Sneha Devi', isActive: true }
        ],
        'team-2': [
            { uniqueId: 'm-3', teamUniqueId: 'team-2', teamName: 'Placement Team B', interviewerUniqueId: 'u-201', interviewerName: 'Akash Verma', isActive: true },
            { uniqueId: 'm-4', teamUniqueId: 'team-2', teamName: 'Placement Team B', interviewerUniqueId: 'u-202', interviewerName: 'Meera Nair', isActive: false }
        ],
        'team-3': [
            { uniqueId: 'm-5', teamUniqueId: 'team-3', teamName: 'Backup Team', interviewerUniqueId: 'u-301', interviewerName: 'John Mathew', isActive: true }
        ]
    };

    constructor() {
        super();
    }

    override findAll(searchParam: any): Observable<PageResponse<TeamItem>> {
        const term = (searchParam?.searchTerm || '').toLowerCase();
        const content = term
            ? this.teams.filter(team => team.name.toLowerCase().includes(term))
            : [...this.teams];

        return of({
            content,
            page: {
                number: searchParam?.page || 0,
                size: searchParam?.size || 10,
                totalElements: content.length,
                totalPages: 1
            }
        }).pipe(delay(250));
    }

    override find(id: string): Observable<TeamItem> {
        const team = this.teams.find(item => item.uniqueId === id) || this.teams[0];
        return of(team).pipe(delay(200));
    }

    override create(data: Partial<TeamItem>): Observable<TeamItem> {
        return this.createOrUpdate({
            uniqueId: data.uniqueId,
            name: data.name || 'New Team',
            description: data.description,
            isActive: data.isActive ?? true,
            members: data.members?.map(member => ({
                uniqueId: member.uniqueId,
                interviewerUniqueId: member.interviewerUniqueId,
                isActive: member.isActive
            })) || []
        });
    }

    override update(id: string, data: Partial<TeamItem>): Observable<TeamItem> {
        return this.createOrUpdate({
            uniqueId: id,
            name: data.name || 'Updated Team',
            description: data.description,
            isActive: data.isActive ?? true,
            members: data.members?.map(member => ({
                uniqueId: member.uniqueId,
                interviewerUniqueId: member.interviewerUniqueId,
                isActive: member.isActive
            })) || []
        });
    }

    override delete(id: string): Observable<void> {
        this.teams = this.teams.filter(item => item.uniqueId !== id);
        delete this.membersByTeam[id];
        return of(void 0).pipe(delay(200));
    }

    override getTeams(params?: any): Observable<TeamItem[]> {
        return of([...this.teams]).pipe(delay(250));
    }

    override getTeamMembers(teamUniqueId: string, params?: any): Observable<TeamMemberItem[]> {
        return of([...(this.membersByTeam[teamUniqueId] || [])]).pipe(delay(250));
    }

    override getInterviewers(params?: any): Observable<TeamInterviewerItem[]> {
        const term = (params?.searchTerm || '').toLowerCase();
        const filtered = term
            ? this.interviewers.filter(interviewer =>
                interviewer.interviewerName.toLowerCase().includes(term) ||
                (interviewer.interviewType || '').toLowerCase().includes(term) ||
                (interviewer.skills || []).some(skill => skill.toLowerCase().includes(term))
            )
            : [...this.interviewers];

        return of(filtered).pipe(delay(250));
    }

    override createOrUpdate(payload: TeamUpsertPayload): Observable<TeamItem> {
        const teamId = payload.uniqueId || `team-${Math.random().toString(36).slice(2, 8)}`;
        const existingIndex = this.teams.findIndex(item => item.uniqueId === teamId);

        const teamName = payload.name || 'Unnamed Team';
        const updatedTeam: TeamItem = {
            uniqueId: teamId,
            name: teamName,
            description: payload.description,
            isActive: payload.isActive,
            members: payload.members.map(member => ({
                uniqueId: member.uniqueId || `m-${Math.random().toString(36).slice(2, 8)}`,
                teamUniqueId: teamId,
                teamName,
                interviewerUniqueId: member.interviewerUniqueId,
                interviewerName: this.resolveInterviewerName(member.interviewerUniqueId),
                isActive: member.isActive
            }))
        };

        if (existingIndex > -1) {
            this.teams[existingIndex] = updatedTeam;
        } else {
            this.teams.push(updatedTeam);
        }

        this.membersByTeam[teamId] = [...(updatedTeam.members || [])];
        return of(updatedTeam).pipe(delay(250));
    }

    private resolveInterviewerName(interviewerUniqueId: string): string {
        const interviewer = this.interviewers.find(item => item.uniqueId === interviewerUniqueId);
        return interviewer?.interviewerName || `Interviewer ${interviewerUniqueId}`;
    }
}
